import { Trans, useTranslation } from 'react-i18next';

import { type IFormulaParams, type TournamentFormulas, TournamentFormulas as TournamentFormulasEnum } from 'features/campaigns';
import TransWithFallback from '../../../../../../../../shared/components/Translations/TransWrapper';
import { findTranslationKeyAsGivenJSONTranslationFallback } from '../../../../../../../../shared/utils/helpers';

import styles from './TournamentRulesContent.module.css';

// Keeps '(x + y) / z = n' on one line by swapping the spaces inside the formula for non-breaking spaces.
const FORMULA_RE = /[(（][\d.,\s+×xX÷/]+[)）][\d.,\s×xX÷/=+]*\d/;
// A line must never start with '=': glue it to the word before it. The line may still break after it.
const EQUALS_RE = /\s+=/g;
const LEADING_EQUALS_RE = /\s+=\s+(?=[(（])/;

const NoBreakFormula = ({ i18nKey, values }: { i18nKey: string; values: Record<string, unknown> }) => {
  const { t } = useTranslation();
  // Missing key -> interpolate the English text (same fallback as TransWithFallback) instead of showing the key.
  const translated = t(i18nKey, { ...values, defaultValue: findTranslationKeyAsGivenJSONTranslationFallback(i18nKey) ?? '' });
  const text = (translated === i18nKey ? '' : translated)
    .replace(LEADING_EQUALS_RE, '\u00A0= ')
    .replace(EQUALS_RE, '\u00A0=')
    .replace(FORMULA_RE, (match) => match.replace(/\s/g, '\u00A0'));
  return <Trans defaults={text} />;
};

const WINNINGS_PLUS_NUMBER_OF_SPINS = ({ currency }: { currency: string }) => {
  return (
    <>
      <ul className={styles['list']}>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.spins_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ numberOfSpins: 1, numberOfPoints: 1 }}
          />
        </li>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.currency_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ amount: 1, currency, numberOfPoints: 1 }}
          />
        </li>
      </ul>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey="tournaments.rules.points_rounded_decimal_text" />
      </span>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 3 }}
        />
        <TransWithFallback i18nKey="tournaments.rules.points_from_win" values={{ numberOfPoints: 3 }} />;{' '}
        <NoBreakFormula i18nKey="tournaments.rules.points_from_spin" values={{ numberOfPoints: 1 }} />;{' '}
        <TransWithFallback
          i18nKey="tournaments.rules.total_points"
          values={{ totalPoints: 4 }}
          components={{ span: <span className={styles['noWrap']} /> }}
        />
      </span>
    </>
  );
};

const WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS = ({ currency }: { currency: string }) => {
  return (
    <>
      <ul className={styles['list']}>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.spins_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ numberOfSpins: 1, numberOfPoints: 1 }}
          />
        </li>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.currency_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ amount: 1, currency, numberOfPoints: 100 }}
          />
        </li>
      </ul>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 3 }}
        />
        <TransWithFallback i18nKey="tournaments.rules.points_from_win" values={{ numberOfPoints: 300 }} />;{' '}
        <TransWithFallback i18nKey="tournaments.rules.points_from_spin" values={{ numberOfPoints: 1 }} />;{' '}
        <TransWithFallback
          i18nKey="tournaments.rules.total_points"
          values={{ totalPoints: 301 }}
          components={{ span: <span className={styles['noWrap']} /> }}
        />
      </span>
    </>
  );
};

const BET_IN_COINS = ({ currency }: { currency: string }) => {
  return (
    <>
      <ul className={styles['list']}>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.bet_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ amount: 1, currency, numberOfPoints: 100 }}
          />
        </li>
      </ul>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 3 }}
        />
        <TransWithFallback i18nKey="tournaments.rules.points_from_bet" values={{ numberOfPoints: 200 }} />.
      </span>
    </>
  );
};

const WINNINGS_D_BETS = ({ currency }: { currency: string }) => {
  return (
    <>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey={'tournaments.rules.winnings_divided_by_bet_text'} />
      </span>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 8 }}
        />
        <TransWithFallback
          i18nKey="tournaments.rules.total_points"
          values={{ totalPoints: 4 }}
          components={{ span: <span className={styles['noWrap']} /> }}
        />
      </span>
    </>
  );
};


const WINNINGS_D_BETS_X_100 = ({ currency }: { currency: string }) => {
  return (
    <>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey={'tournaments.rules.winnings_divided_by_bets_multiplied_by_100_text'} />
      </span>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 1 }}
        />
        <TransWithFallback
          i18nKey="tournaments.rules.total_points"
          values={{ totalPoints: 50 }}
          components={{ span: <span className={styles['noWrap']} /> }}
        />
      </span>
    </>
  );
};

const WINNINGS_PLUS_BETS = ({ currency }: { currency: string }) => {
  return (
    <>
      <ul className={styles['list']}>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.currency_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ amount: 1, currency, numberOfPoints: 100 }}
          />
        </li>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.bet_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ amount: 1, currency, numberOfPoints: 100 }}
          />
        </li>
      </ul>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 8 }}
        />
        <TransWithFallback i18nKey="tournaments.rules.points_from_win" values={{ numberOfPoints: 800 }} />;{' '}
        <TransWithFallback i18nKey="tournaments.rules.points_from_bet" values={{ numberOfPoints: 200 }} />;{' '}
        <TransWithFallback
          i18nKey="tournaments.rules.total_points"
          values={{ totalPoints: 1000 }}
          components={{ span: <span className={styles['noWrap']} /> }}
        />
      </span>
    </>
  );
};

const WINNINGS_X_5_PLUS_NUMBER_OF_SPINS = ({ currency }: { currency: string }) => {
  return (
    <>
      <ul className={styles['list']}>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.spins_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ numberOfSpins: 1, numberOfPoints: 1 }}
          />
        </li>
        <li>
          <TransWithFallback
            i18nKey="tournaments.rules.currency_win_rule"
            components={{ span: <span className={styles['highlightedText']} /> }}
            values={{ amount: 1, currency, numberOfPoints: 100 }}
          />
        </li>
      </ul>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey={'tournaments.rules.points_rounded_text'} />
      </span>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey="tournaments.rules.winnings_multiplied_by_five_text" values={{ currency }} />
      </span>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.rules_example_formula"
          values={{ betAmount: 2, currency, winAmount: 8 }}
        />
        <TransWithFallback
          i18nKey="tournaments.rules.winnings_in_points_multiplied_by_five"
          values={{ value1: 800, value2: 5, value3: 4000 }}
        />
        ; <TransWithFallback i18nKey="tournaments.rules.points_from_spin" values={{ numberOfPoints: 1 }} />;{' '}
        <TransWithFallback
          i18nKey="tournaments.rules.total_points"
          values={{ totalPoints: 4001 }}
          components={{ span: <span className={styles['noWrap']} /> }}
        />
      </span>
    </>
  );
};

const WINNINGS_PLUS_BETS_D_100 = ({ currency }: { currency: string }) => {
  return (
    <>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey="tournaments.rules.winnings_plus_bets_divided_by_100_text" values={{ currency }} />
      </span>
      <span className={styles['styledSpan']}>
        <NoBreakFormula
          i18nKey="tournaments.rules.winnings_plus_bets_divided_by_100_example"
          values={{ betAmount: 20, currency, winAmount: 80, totalPoints: 1 }}
        />
      </span>
    </>
  );
};

const WINNINGS_X_5_PLUS_BETS = ({ currency }: { currency: string }) => {
  return (
    <>
      <span className={styles['italicText']}>
        <TransWithFallback
          i18nKey="tournaments.rules.winnings_multiplied_by_5_plus_bets_divided_by_100_text"
          values={{ currency }}
        />
      </span>
      <span className={styles['styledSpan']}>
        <NoBreakFormula
          i18nKey="tournaments.rules.winnings_multiplied_by_5_plus_bets_divided_by_100_example"
          values={{ betAmount: 25, currency, winAmount: 15, totalPoints: 1 }}
        />
      </span>
    </>
  );
};

const WINNINGS_D_BETS_X_100_PLUS_SPINS = ({ currency }: { currency: string }) => {
  return (
    <>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey="tournaments.rules.winnings_divided_by_bets_multiplied_by_100_plus_spins_text" />
      </span>
      <span className={styles['styledSpan']}>
        <NoBreakFormula
          i18nKey="tournaments.rules.winnings_divided_by_bets_multiplied_by_100_plus_spins_example"
          values={{ betAmount: 20, currency, winAmount: 80, winPoints: 400, totalPoints: 401 }}
        />
      </span>
    </>
  );
};

const BET_IN_COINS_COEF = ({
  currency,
  formulaParams,
}: {
  currency: string;
  formulaParams: IFormulaParams | undefined;
}) => {
  const numberOfPoints = 3;

  const coef = formulaParams?.coef;
  if (coef === undefined) return null;

  return (
    <>
      <span className={styles['italicText']}>
        <TransWithFallback i18nKey={'tournaments.rules.bet_amount_divided_by_coef_text'} values={{ currency, coef }} />
      </span>
      <span className={styles['styledSpan']}>
        <TransWithFallback
          i18nKey="tournaments.rules.example_divided_by_coef_result_text"
          values={{
            amount: numberOfPoints * coef,
            currency,
            coef,
            numberOfPoints,
          }}
        />
      </span>
    </>
  );
};

const formulaMap = {
  [TournamentFormulasEnum.WINNINGS_PLUS_NUMBER_OF_SPINS]: WINNINGS_PLUS_NUMBER_OF_SPINS,
  [TournamentFormulasEnum.WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS]: WINNINGS_IN_COINS_PLUS_NUMBER_OF_SPINS,
  [TournamentFormulasEnum.BET_IN_COINS]: BET_IN_COINS,
  [TournamentFormulasEnum.WINNINGS_D_BETS]: WINNINGS_D_BETS,
  [TournamentFormulasEnum.WINNINGS_D_BETS_X_100]: WINNINGS_D_BETS_X_100,
  [TournamentFormulasEnum.WINNINGS_PLUS_BETS]: WINNINGS_PLUS_BETS,
  [TournamentFormulasEnum.WINNINGS_X_5_PLUS_NUMBER_OF_SPINS]: WINNINGS_X_5_PLUS_NUMBER_OF_SPINS,
  [TournamentFormulasEnum.BET_IN_COINS_COEF]: BET_IN_COINS_COEF,
  [TournamentFormulasEnum.WINNINGS_PLUS_BETS_D_100]: WINNINGS_PLUS_BETS_D_100,
  [TournamentFormulasEnum.WINNINGS_X_5_PLUS_BETS]: WINNINGS_X_5_PLUS_BETS,
  [TournamentFormulasEnum.WINNINGS_D_BETS_X_100_PLUS_SPINS]: WINNINGS_D_BETS_X_100_PLUS_SPINS,
};

interface IFormulaProps {
  formula: TournamentFormulas;
  formulaParams?: IFormulaParams | undefined;
  currency: string;
}

const RulesFormula: React.FC<IFormulaProps> = ({ formula, formulaParams, currency }) => {
  const FormulaComponent = formulaMap[formula];
  return FormulaComponent ? <FormulaComponent currency={currency} formulaParams={formulaParams} /> : null;
};

export default RulesFormula;
